import { ReactNode, useEffect } from "react"
import { UseFormReturn, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CapabilityKind, GeneratedRequirement, InstructionCapabilityConfigSchema } from "schema"
import { toast } from "sonner"
import z from "zod"
import { useCapabilityVariables } from "../../hooks/useCapabilityVariables"
import { useExtractCapabilityRequirementsMutation } from "../../hooks/useExtractCapabilityRequirementsMutation"
import { ActivationAccordion } from "./Shared/ActivationAccordion"
import { AgentAccordion } from "./Shared/AgentAccordion"
import { CampaignActivationAccordion } from "./Shared/CampaignActivationAccordion"
import { GeneralSection } from "./Shared/GeneralSection"

type Props = {
  form: UseFormReturn<z.infer<typeof InstructionCapabilityConfigSchema>>
  activationSettings: ReactNode
  requirements: GeneratedRequirement[]
  requirementsAgentPrompt: string | null
  onRequirementsChange: (requirements: GeneratedRequirement[]) => void
  onRequirementsAgentPromptChange: (agentPrompt: string | null) => void
}

export function InstructionCapabilityForm({
  form,
  activationSettings,
  requirements,
  requirementsAgentPrompt,
  onRequirementsChange,
  onRequirementsAgentPromptChange,
}: Props) {
  const { t } = useTranslation()
  const dataToExtract = useWatch({ control: form.control, name: "dataToExtract" })
  const name = useWatch({ control: form.control, name: "name" })
  const activationPrompt = useWatch({ control: form.control, name: "activationPrompt" })
  const agentPrompt = useWatch({ control: form.control, name: "agentPrompt" })
  const { mutateAsync: extractRequirements, isPending } = useExtractCapabilityRequirementsMutation()

  useEffect(() => {
    if (requirementsAgentPrompt === null || agentPrompt.trim() === requirementsAgentPrompt) return
    onRequirementsChange([])
    onRequirementsAgentPromptChange(null)
  }, [agentPrompt, requirementsAgentPrompt, onRequirementsChange, onRequirementsAgentPromptChange])

  const handleExtractRequirements = async () => {
    try {
      const response = await extractRequirements({
        capabilityName: name.trim(),
        capabilityKind: CapabilityKind.Instruction,
        capabilityContext: buildInstructionCapabilityContext({
          activationPrompt,
          dataToExtract,
        }),
        agentInstructions: agentPrompt.trim(),
      })
      onRequirementsChange(response.requirements)
      onRequirementsAgentPromptChange(agentPrompt.trim())
    } catch {
      toast.error(t("capabilities.instructionConfig.requirements.error"))
    }
  }

  // Variables available at each step:
  // - Activation: only customer (extraction hasn't happened yet)
  // - Agent: customer + extracted data
  const customerOnlyVariables = useCapabilityVariables([], null) // Just customer fields
  const allVariables = useCapabilityVariables(dataToExtract, null) // customer + extraction

  return (
    <>
      <GeneralSection form={form} translationPrefix="instructionConfig" />
      <ActivationAccordion
        form={form}
        translationPrefix="instructionConfig"
        variables={customerOnlyVariables}
      />
      <AgentAccordion
        form={form}
        translationPrefix="instructionConfig"
        variables={allVariables}
        requirementsExtraction={{
          requirements,
          onExtract: handleExtractRequirements,
          isPending,
          disabled: !name.trim() || !agentPrompt.trim(),
        }}
      />
      <CampaignActivationAccordion activationSettings={activationSettings} />
    </>
  )
}

function buildInstructionCapabilityContext({
  activationPrompt,
  dataToExtract,
}: Pick<z.infer<typeof InstructionCapabilityConfigSchema>, "activationPrompt" | "dataToExtract">) {
  const fields = dataToExtract.length
    ? dataToExtract
        .map((field) => {
          const type = typeof field.type === "string" ? field.type : field.type.values.join(" | ")
          const requirement = field.required ? "required" : "optional"
          const description = field.description ? `: ${field.description}` : ""
          return `- ${field.name} (${type}, ${requirement})${description}`
        })
        .join("\n")
    : "- None"

  return `This is a passive Instruction capability. It does not perform an external action; after it activates successfully, its agent instructions guide the next customer-facing reply.

Activation condition (context only; do not extract it as a reply requirement):
${activationPrompt.trim()}

Data available to the agent instructions after evaluation:
${fields}`.slice(0, 10000)
}
